import React from 'react';
import clsx from 'clsx';
import { Phone, MessageCircle, Share2 } from 'lucide-react';
import { IconButton } from '../../ui/IconButton';

/**
 * Tab navigation bar (products / about / faq / cancellation) plus contact
 * shortcuts (phone, WhatsApp) and the share button on the right.
 *
 * `tabs` is a list of { id, label, visible } so optional tabs can be hidden
 * upstream (e.g. FAQ only shows when FAQs exist).
 */
export function StoreTabs({ activeTab, onChange, tabs, store, onShare }) {
    return (
        <nav className="z-nav bg-surface/95 border-b border-line-subtle shadow-xs">
            <div className="max-w-5xl mx-auto flex items-center gap-2 px-4 py-2">
                <div className="flex items-center gap-2 min-w-0 flex-1 overflow-x-auto no-scrollbar">
                    {tabs.filter(t => t.visible !== false).map(tab => {
                        const active = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => onChange(tab.id)}
                                aria-pressed={active}
                                className={clsx(
                                    'flex-shrink-0 px-3 py-1.5 rounded-pill text-sm font-semibold transition-colors duration-200',
                                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
                                    active
                                        ? 'text-brand-700 font-bold ring-1 ring-brand-300'
                                        : 'text-brand-800 hover:bg-brand-50',
                                )}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {store.contact_phone && (
                        <>
                            <a
                                href={`tel:${store.contact_phone}`}
                                title={`התקשר: ${store.contact_phone}`}
                                aria-label={`התקשר ל-${store.contact_phone}`}
                                className="inline-flex items-center justify-center w-10 h-10 rounded-pill text-info-700 hover:bg-info-50 transition-colors"
                            >
                                <Phone className="w-5 h-5" />
                            </a>
                            {store.whatsapp_available && (
                                <a
                                    href={`https://wa.me/${store.contact_phone.replace(/[^0-9]/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="שלח הודעת וואטסאפ"
                                    aria-label="שלח הודעת וואטסאפ"
                                    className="inline-flex items-center justify-center w-10 h-10 rounded-pill text-success-700 hover:bg-success-50 transition-colors"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                </a>
                            )}
                        </>
                    )}
                    <IconButton
                        icon={<Share2 />}
                        aria-label="שתף חנות"
                        variant="ghost"
                        tone="brand"
                        shape="round"
                        onClick={onShare}
                    />
                </div>
            </div>
        </nav>
    );
}

export default StoreTabs;
